import { InputCheckbox } from '@creditornot/cb-checkbox';
import { AlertCircleFilled } from '@creditornot/cb-icons';
import { colors, cssThemedValue, WoltLogo } from '@creditornot/cb-ingredients';
import React, { CSSProperties, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { areEqual } from 'react-window';
import styled from 'styled-components';

import { Typography as T } from 'common/typography';

import { MenuCatalogListData } from '../types';

type RowRendererData = {
  list: MenuCatalogListData[];
  allowDuplicateGtins?: boolean;
  selectedItemIds: string[];
  onSelectItemId: (itemId: string) => void;
};

type Props = {
  index: number;
  data: RowRendererData;
  style?: CSSProperties;
};

const ItemRow = styled.div`
  height: 76px;
  display: flex;
  align-items: center;
  padding: 20px 24px;
  border-block-end: 1px solid ${cssThemedValue('borderPrimary')};
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  margin-inline-start: 12px;
`;

const Name = styled(T.Body3)``;

const Thumbnail = styled.img`
  height: 32px;
  width: 32px;
  border-radius: 4px;
  margin-inline-start: 24px;
  object-fit: cover;
`;

const ImgPlaceholder = styled(WoltLogo)`
  padding: 4px;
  height: 32px;
  width: 32px;
  border-radius: 4px;
  margin-inline-start: 24px;
  border: 1px solid ${cssThemedValue('borderPrimary')};
  fill: ${cssThemedValue('textTertiary')};
  background-color: ${cssThemedValue({ light: colors.pepper8, dark: colors.salt12 })};
`;

const ItemInfo = styled(T.Small)`
  display: flex;
  align-items: center;
  color: ${cssThemedValue('textSecondary')};
  > div:not(:first-child) {
    margin-inline-start: 8px;
  }
`;

const StyledAlertCircleFilled = styled(AlertCircleFilled)`
  display: block;
  width: 12px;
  fill: ${colors.banana100};
`;

const MenuEditorListRowRenderer = ({
  index,
  data: { list, allowDuplicateGtins, selectedItemIds, onSelectItemId },
  style,
}: Props) => {
  const row = list[index];

  const isDuplicateGtinSelected = useMemo(() => {
    const listItemsWithDuplicateGtin = list.filter(item => item.gtin && item.gtin === row.gtin);
    if (allowDuplicateGtins) {
      return listItemsWithDuplicateGtin.some(
        listItem => listItem.itemId !== row.itemId && selectedItemIds.includes(listItem.itemId),
      );
    }
    if (selectedItemIds.includes(row.itemId)) {
      return false;
    }
    const hasDuplicateGtin = !!row.gtin && list.filter(entry => entry.gtin === row.gtin).length > 1;
    if (!hasDuplicateGtin) {
      return false;
    }
    return selectedItemIds.some(itemId => list.find(listItem => listItem.itemId === itemId)?.gtin === row.gtin);
  }, [allowDuplicateGtins, list, row.gtin, row.itemId, selectedItemIds]);

  return (
    <ItemRow style={style}>
      <InputCheckbox
        checked={selectedItemIds.includes(row.itemId)}
        disabled={!allowDuplicateGtins && isDuplicateGtinSelected}
        onChange={() => onSelectItemId(row.itemId)}
      />
      {row.image ? <Thumbnail src={row.image} /> : <ImgPlaceholder />}
      <Content>
        <Name>{row.name}</Name>
        <ItemInfo>
          {(!row.hasProductFamilyId || isDuplicateGtinSelected) && <StyledAlertCircleFilled />}
          {!row.hasProductFamilyId && (
            <div>
              <FormattedMessage id="menu.catalog.missing-family" />
            </div>
          )}
          {isDuplicateGtinSelected && (
            <div>
              <FormattedMessage id="menu.catalog.duplicate-gtin-selected" />
            </div>
          )}
          <div>{row.gtin}</div>
        </ItemInfo>
      </Content>
    </ItemRow>
  );
};

export default React.memo(MenuEditorListRowRenderer, areEqual);
